import { Calendar, Heart, TrendingUp, FileText, Bell, Clock, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import useParentData from '@/hooks/useParentData';
import ChildSelector from './ChildSelector';

const ParentHome = () => {
  const { children, selectedChild, setSelectedChild, loading, dashboardData } = useParentData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-600">No children found in your account.</p>
            <p className="text-sm text-gray-500 mt-2">Please contact the hostel administrator.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const metrics = dashboardData?.metrics || {};
  const recentFeed = dashboardData?.recent_feed || [];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    const colors = {
      present: 'text-green-600 bg-green-50',
      absent: 'text-red-600 bg-red-50',
      'half-day': 'text-yellow-600 bg-yellow-50',
      healthy: 'text-green-600 bg-green-50',
      sick: 'text-red-600 bg-red-50',
      injured: 'text-orange-600 bg-orange-50'
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome back! Here's what's happening with your child</p>
      </div>

      {/* Child Selector */}
      <ChildSelector 
        children={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
      />

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Attendance Card */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Attendance
            </CardTitle>
            <Calendar className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
              {metrics.attendance?.percentage || 0}%
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {metrics.attendance?.present_days || 0}/{metrics.attendance?.total_days || 0} days present
            </p>
            {metrics.attendance?.latest && (
              <div className={`mt-2 inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(metrics.attendance.latest.status)}`}>
                Today: {metrics.attendance.latest.status}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Health Card */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Health Status
            </CardTitle>
            <Heart className="w-5 h-5 text-red-600" />
          </CardHeader>
          <CardContent>
            {metrics.health?.latest ? (
              <>
                <div className={`inline-block px-3 py-1 rounded-lg text-lg font-semibold capitalize ${getStatusColor(metrics.health.latest.status)}`}>
                  {metrics.health.latest.status}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Updated: {formatDate(metrics.health.latest.date)}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">No recent updates</p>
            )}
          </CardContent>
        </Card>

        {/* Activities Card */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Activities
            </CardTitle>
            <Activity className="w-5 h-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
              {metrics.activities?.count || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This week
            </p>
            {metrics.activities?.recent && metrics.activities.recent.length > 0 && (
              <p className="text-xs text-gray-600 mt-2 truncate">
                Latest: {metrics.activities.recent[0].title || metrics.activities.recent[0].activity_name}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Latest Result
            </CardTitle>
            <FileText className="w-5 h-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            {metrics.results?.latest ? (
              <>
                <div className="text-lg font-bold truncate" style={{ color: '#2C5F7F' }} title={metrics.results.latest.exam_type || metrics.results.latest.exam_name}>
                  {metrics.results.latest.exam_type || metrics.results.latest.exam_name}
                </div>
                <div className="text-2xl font-extrabold mt-1 text-purple-600">
                  {metrics.results.latest.overall_percentage !== undefined ? `${metrics.results.latest.overall_percentage}%` : `${metrics.results.latest.marks_obtained || 0}/${metrics.results.latest.total_marks || 0}`}
                </div>
                {metrics.results.latest.grade && (
                  <p className="text-xs text-gray-500 mt-1">
                    Grade: {metrics.results.latest.grade} ({metrics.results.latest.status})
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Held on: {formatDate(metrics.results.latest.exam_date)}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">No results yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Unread Notices */}
      {dashboardData?.unread_notices > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">
                  You have {dashboardData.unread_notices} unread notice{dashboardData.unread_notices > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-blue-700">Check the Notice Board for updates</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Recent Updates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentFeed.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No recent updates</p>
          ) : (
            <div className="space-y-3">
              {recentFeed.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className={`p-2 rounded-lg ${
                    item.type === 'attendance' ? 'bg-blue-100' :
                    item.type === 'health' ? 'bg-red-100' :
                    'bg-green-100'
                  }`}>
                    {item.type === 'attendance' && <Calendar className="w-4 h-4 text-blue-600" />}
                    {item.type === 'health' && <Heart className="w-4 h-4 text-red-600" />}
                    {item.type === 'activity' && <Activity className="w-4 h-4 text-green-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.title}</p>
                    <p className="text-xs text-gray-600 capitalize">{item.description}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(item.date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentHome;
